<template>
  <div class="MyMapFondLayerCard" v-show="isShown">
    <div class="MyMapFondLayerCard__content">
      <div class="MyMapFondLayerCard__icon">
        <Icon icon="mdi:map-marker-path" color="main-blue" :size="24" />
      </div>
      <div class="MyMapFondLayerCard__info">
        <span class="MyMapFondLayerCard__title">{{ metadata.label }}</span>
        <p class="MyMapFondLayerCard__description">
          {{ metadata.description }}
        </p>
      </div>
      <button
        class="MyMapFondLayerCard__toggleButton"
        @click="toggleVisibility"
        :aria-label="
          isVisible
            ? $t('myMap.cadastreSoa.hide', 'Masquer la couche')
            : $t('myMap.cadastreSoa.show', 'Afficher la couche')
        "
        type="button"
      >
        <Icon
          :icon="isVisible ? 'mdi:eye' : 'mdi:eye-off'"
          :color="isVisible ? 'main-blue' : 'grey'"
          :size="20"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  FondLayerStyleMetadata,
  FondLayerVisibleIn,
} from '@/models/interfaces/map/FondLayerStyle'
import { useMyMapStore } from '@/stores/myMapStore'
import Icon from '@/components/ui/Icon.vue'
import { computed } from 'vue'

const props = defineProps<{
  styleId: string
  metadata: FondLayerStyleMetadata
}>()

const mapStore = useMyMapStore()

function isVisibleInCurrentContext(visibleIn: FondLayerVisibleIn): boolean {
  const atlasActive = mapStore.activeAtlas.leftPanel.active
  const hasCommune = mapStore.mapSearch != null
  if (visibleIn === 'always') return !atlasActive
  if (visibleIn === 'commune_only') return !atlasActive && hasCommune
  if (visibleIn === 'atlas_only') return atlasActive
  return false
}

/** Card is shown when metadata.visible_in matches current view (central / commune / atlas). */
const isShown = computed(() => isVisibleInCurrentContext(props.metadata.visible_in))

/** True when the user has turned this layer on via the toggle. Layer is loaded only when active. */
const isVisible = computed(() => mapStore.visibleFondStyleIds.includes(props.styleId))

function toggleVisibility(): void {
  if (!mapStore.myMap) return
  mapStore.setFondLayerVisible(props.styleId, !isVisible.value)
}
</script>

<style lang="scss">
.MyMapFondLayerCard {
  display: flex;
  flex-flow: column nowrap;
  border-radius: var(--dim-radius);
  border: 1px solid var(--neutral-border);
  padding: 0.75rem 1rem;
  min-height: var(--panel-row-min-height);
  background-color: var(--neutral-surface-subtle);
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--neutral-hover);
  }

  &__content {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 0.75rem;
    min-height: var(--panel-row-min-height);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    gap: 0.125rem;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: var(--panel-row-title-size);
    font-weight: var(--panel-row-title-weight);
    color: rgb(var(--v-theme-main-blue));
  }

  &__description {
    font-size: var(--panel-label-size);
    color: var(--panel-label-color);
    margin: 0;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__toggleButton {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2.75rem;
    min-height: 2.75rem;
    width: 2.75rem;
    height: 2.75rem;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: var(--dim-radius);
    transition: background-color 0.2s;
    flex-shrink: 0;
    padding: 0;

    &:hover {
      background-color: var(--neutral-hover);
    }

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-main-blue));
      outline-offset: 2px;
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.08);
    }
  }
}
</style>
